import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { cn } from "@/libs/utils";
import useEmblaCarousel from "embla-carousel-react";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import ChevronLeftIcon from "@/v2-components/core/icon/chevron-left-icon";
import { EmblaOptionsType, EmblaPluginType } from "embla-carousel";

interface DisplayGameCardsCarouselProps
  extends React.PropsWithChildren, React.HTMLProps<HTMLDivElement> {
  isLoading?: boolean;
  className?: string;
  offset?: number;
  onResize?: () => void;
  onReInit?: () => void;
  plugins?: EmblaPluginType[];
  options?: EmblaOptionsType;
  enableNavigations?: boolean;
}

export const DisplayGameCardsCarousel = forwardRef(
  (
    {
      children,
      className,
      offset = 0,
      isLoading = false,
      onResize,
      onReInit,
      plugins = [],
      options = {},
      enableNavigations = true,
      ...rest
    }: DisplayGameCardsCarouselProps,
    forwardedRef,
  ) => {
    const [current, setCurrent] = useState(0); // 강제렌더링
    const [slides, setSlides] = useState<HTMLElement[]>([]); // 강제렌더링

    const [ref, api] = useEmblaCarousel(
      {
        align: "start",
        loop: false,
        containScroll: "trimSnaps",
        skipSnaps: true,
        ...options,
      },
      plugins,
    );

    useImperativeHandle(forwardedRef, () => api, [api]);

    useEffect(() => {
      if (!api) return;
      setCurrent(api.selectedScrollSnap());
      setSlides(api.slideNodes());
      onReInit?.();
      onResize?.();
      const handleSelect = () => {
        setCurrent(api.selectedScrollSnap());
      };
      const handleSlidesChanged = () => {
        setSlides(api.slideNodes());
      };
      const handleResize = () => {
        onResize?.();
      };
      const handleReInit = () => {
        api.scrollTo(0);
        onReInit?.();
      };
      api.on("select", handleSelect);
      api.on("slidesChanged", handleSlidesChanged);
      api.on("resize", handleResize);
      api.on("reInit", handleReInit);
      return () => {
        api.off("select", handleSelect);
        api.off("slidesChanged", handleSlidesChanged);
        api.off("resize", handleResize);
        api.off("reInit", handleReInit);
      };
    }, [api, onReInit, onResize]);

    return (
      <>
        <div {...rest} className={cn(className)}>
          <div className="relative overflow-visible">
            <div className="relative overflow-hidden" ref={ref}>
              {isLoading && <Loading />}
              {children}
            </div>
            {!!enableNavigations && api && api.canScrollNext() && (
              <button
                onClick={() => {
                  api.scrollNext();
                }}
                className="absolute right-3 top-0 hidden laptop:block max_64:-right-6"
                style={{ transform: `translate3d(0, ${offset}px, 0)` }}
              >
                <Next />
              </button>
            )}
            {!!enableNavigations && api && api.canScrollPrev() && (
              <button
                onClick={() => {
                  api.scrollPrev();
                }}
                className="absolute left-3 top-0 hidden laptop:block max_64:-left-6"
                style={{ transform: `translate3d(0, ${offset}px, 0)` }}
              >
                <Prev />
              </button>
            )}
          </div>
        </div>
      </>
    );
  },
);

DisplayGameCardsCarousel.displayName = "DisplayGameCardsCarousel";

const Next = () => (
  <div className="aspect-square h-12 -translate-y-1/2 rounded-full bg-coolgray-10 hover:bg-coolgray-hover-20">
    <ChevronRightIcon className="h-full w-full text-coolgray-40" />
  </div>
);

const Prev = () => (
  <div className="aspect-square h-12 -translate-y-1/2 rounded-full bg-coolgray-10 hover:bg-coolgray-hover-20">
    <ChevronLeftIcon className="h-full w-full text-coolgray-40" />
  </div>
);

const Loading = () => (
  <div className="flex min-h-[200px] w-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-coolgray-10 border-t-transparent" />
  </div>
);
